/**
 * Location tag id → name lookup for list rows and editors (#999).
 */

import { useCallback, useMemo } from 'react';

import { useOptionalLocationTagsDraft } from '../contexts/LocationTagsDraftContext';
import { useLocationTags } from './queries/useLocationTags';
import {
  buildLocationTagNameById,
  resolveLocationTagName,
} from '../utils/locationTagLookup';
import {
  isDraftLocationTagRequestId,
  isOfflineClientRequestId,
  isPersistedServerRequestId,
} from '../utils/locationTagRequestId';

import type { LocationTag } from '../types/locationTag';

export function useLocationTagNameLookup(requestId?: string | null) {
  const draftContext = useOptionalLocationTagsDraft();
  const isDraftMode =
    draftContext != null && isDraftLocationTagRequestId(requestId);
  const canUseServerTags =
    isPersistedServerRequestId(requestId) ||
    isOfflineClientRequestId(requestId);

  const { data: serverTags = [] } = useLocationTags(
    requestId ?? '',
    canUseServerTags && !isDraftMode,
  );

  const tags: LocationTag[] = useMemo(() => {
    if (isDraftMode) {
      return draftContext!.draftTags.map(draft => ({
        id: draft.clientId,
        requestId: 0,
        name: draft.name,
      }));
    }
    return serverTags;
  }, [draftContext, isDraftMode, serverTags]);

  const nameById = useMemo(() => buildLocationTagNameById(tags), [tags]);

  const resolveName = useCallback(
    (locationTagId: number | null | undefined) =>
      resolveLocationTagName(locationTagId, nameById),
    [nameById],
  );

  return { resolveName, nameById, tags };
}
